'use client';

/**
 * @file Overlay.tsx
 * @description Development-only floating UI overlay for the API Visual Debugger.
 *
 * - Returns null immediately in production (process.env.NODE_ENV !== 'development').
 * - Reads config.position from the store to dynamically position badge + panel.
 * - Shows a compact floating badge (active/total count) that expands into a
 *   tabbed detail panel (All / Pending / Success / Error).
 * - Each request row expands to show full URL, timing, and formatted call stack.
 *
 * SSR Bridge (added):
 * - Polls GET /api/dev/api-debugger every 1 000 ms to fetch SSR request logs.
 * - Merges SSR + Client requests into a single sorted list (newest first).
 * - "Clear" now also sends DELETE /api/dev/api-debugger to wipe the server cache.
 * - Footer shows per-environment breakdown (SSR / Client counts).
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiDebuggerStore } from './store';
import { extractTrigger, formatStackFrames, parseStackTrace } from './utils';

import type { ApiRequest, ApiRequestStatus } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-sky-500/20 text-sky-300 border border-sky-500/30',
  POST: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
  PUT: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
  PATCH: 'bg-violet-500/20 text-violet-300 border border-violet-500/30',
  DELETE: 'bg-rose-500/20 text-rose-300 border border-rose-500/30',
  HEAD: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
};

const STATUS_COLORS: Record<ApiRequestStatus, string> = {
  pending: 'text-amber-400',
  success: 'text-emerald-400',
  error: 'text-rose-400',
};

const STATUS_ICONS: Record<ApiRequestStatus, string> = {
  pending: '⏳',
  success: '✓',
  error: '✕',
};

/** Polling interval in milliseconds for fetching SSR requests from the server. */
const SSR_POLL_INTERVAL_MS = 1_000;

type FilterTab = 'all' | ApiRequestStatus;

// ─── Sub-components ───────────────────────────────────────────────────────────

function MethodBadge({ method }: { method: string }) {
  const cls =
    METHOD_COLORS[method.toUpperCase()] ??
    'bg-slate-500/20 text-slate-300 border border-slate-500/30';
  return (
    <span
      className={`
        inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-[10px] leading-none font-bold
        ${cls}
      `}
    >
      {method.toUpperCase()}
    </span>
  );
}

function EnvPill({ env }: { env: 'SSR' | 'Client' }) {
  return (
    <span
      className={`
        inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] leading-none font-semibold
        ${
          env === 'SSR'
            ? 'border border-purple-500/30 bg-purple-500/20 text-purple-300'
            : 'text-cyan-300 border border-cyan-500/30 bg-cyan-500/20'
        }
      `}
    >
      {env}
    </span>
  );
}

function PulsingDot() {
  return (
    <span className="relative flex size-2">
      <span className="bg-amber-400 absolute inline-flex size-full animate-ping rounded-full opacity-75" />
      <span className="bg-amber-400 relative inline-flex size-2 rounded-full" />
    </span>
  );
}

const RequestRow = memo(function RequestRow({ req }: { req: ApiRequest }) {
  const [expanded, setExpanded] = useState(false);

  const shortUrl = useMemo(() => {
    try {
      const u = new URL(req.url);
      return (
        u.pathname +
        (u.search
          ? u.search.slice(0, 40) + (u.search.length > 40 ? '…' : '')
          : '')
      );
    } catch {
      return req.url;
    }
  }, [req.url]);

  // ── Lazy stack parsing ─────────────────────────────────────────────────────
  // The interceptor deliberately does **not** run `parseStackTrace` /
  // `extractTrigger` synchronously on the request hot path — those regex
  // walks are one of the primary contributors to main-thread stalls under
  // heavy concurrent load. Instead we parse here at render time, and only
  // once per RequestRow (which is `memo`'d on `req`, and `req` objects are
  // immutable after `updateRequest` patches them once).
  const stackFrames = useMemo(
    () => req.stackFrames ?? parseStackTrace(req.stackTrace),
    [req.stackFrames, req.stackTrace],
  );

  const trigger = useMemo(
    () => req.trigger ?? extractTrigger(req.stackTrace),
    [req.trigger, req.stackTrace],
  );

  const formattedStack = useMemo(
    () =>
      stackFrames.length > 0
        ? formatStackFrames(stackFrames)
        : req.stackTrace,
    [stackFrames, req.stackTrace],
  );

  return (
    <div
      className="
        border-b border-white/5
        last:border-0
      "
    >
      {/* Row summary */}
      <button
        type="button"
        className="
          flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors
          hover:bg-white/5
          active:bg-white/10
        "
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Status icon */}
        <span
          className={`
            mt-px shrink-0 text-xs font-bold
            ${STATUS_COLORS[req.status]}
          `}
        >
          {STATUS_ICONS[req.status]}
        </span>

        {/* Method + URL */}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <MethodBadge method={req.method} />
            <EnvPill env={req.environment} />
            {req.statusCode !== null && (
              <span
                className={`
                  font-mono text-[10px] font-semibold
                  ${
                    req.statusCode >= 400 ? 'text-rose-400' : 'text-emerald-400'
                  }
                `}
              >
                {req.statusCode}
              </span>
            )}
            {req.duration !== null && (
              <span className="text-slate-400 font-mono text-[10px]">
                {req.duration}ms
              </span>
            )}
          </div>
          <p className="text-slate-300 truncate font-mono text-[11px]">
            {shortUrl}
          </p>
          {trigger && trigger !== 'Unknown' && (
            <p className="text-indigo-400 font-mono text-[9px] mt-0.5">⚡ {trigger}</p>
          )}
        </div>

        {/* Expand chevron */}
        <span
          aria-hidden
          className={`
            text-slate-500 mt-0.5 shrink-0 transition-transform duration-200
            ${expanded ? 'rotate-180' : ''}
          `}
        >
          ▾
        </span>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="space-y-2 border-t border-white/5 bg-black/30 px-3 py-2.5">
          {/* Full URL */}
          <div>
            <p className="text-slate-500 mb-1 text-[9px] font-semibold tracking-widest uppercase">
              Full URL
            </p>
            <p className="text-slate-300 font-mono text-[10px] break-all">
              {req.url}
            </p>
          </div>

          {/* Timing */}
          <div className="flex gap-4">
            <div>
              <p className="text-slate-500 mb-0.5 text-[9px] font-semibold tracking-widest uppercase">
                Started
              </p>
              <p className="text-slate-300 font-mono text-[10px]">
                {new Date(req.startedAt).toLocaleTimeString([], {
                  hour12: false,
                })}
              </p>
            </div>
            {req.duration !== null && (
              <div>
                <p className="text-slate-500 mb-0.5 text-[9px] font-semibold tracking-widest uppercase">
                  Duration
                </p>
                <p className="text-slate-300 font-mono text-[10px]">
                  {req.duration}ms
                </p>
              </div>
            )}
          </div>

          {/* Call stack */}
          {formattedStack && (
            <div>
              <p className="text-slate-500 mb-1 text-[9px] font-semibold tracking-widest uppercase">
                Call Stack
              </p>
              <pre className="text-emerald-300/80 overflow-x-auto rounded-sm bg-black/50 p-2 font-mono text-[10px] leading-relaxed ring-1 ring-white/5">
                {formattedStack}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * The top-level overlay component. Renders null in production.
 * Mount once in your root layout (e.g. app/[locale]/layout.tsx).
 */
export function ApiDebuggerOverlay() {
  return <ApiDebuggerInner />;
}

// ─── Inner Component (only rendered in dev) ───────────────────────────────────

function ApiDebuggerInner() {
  const requests = useApiDebuggerStore((s) => s.requests);
  const ssrRequests = useApiDebuggerStore((s) => s.ssrRequests);
  const setSsrRequests = useApiDebuggerStore((s) => s.setSsrRequests);
  const clearRequests = useApiDebuggerStore((s) => s.clearRequests);
  const enabled = useApiDebuggerStore((s) => s.config.enabled);

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => setIsMounted(true), []);

  // ── Drag state ──────────────────────────────────────────────────────────────
  // Initial position: left-middle of the screen (left: 16px, top: 50vh).
  const [pos, setPos] = useState<{ x: number; y: number }>(() => ({
    x: 16,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 300,
  }));
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  // Store exact mousedown coords so we can measure true travel distance.
  const dragStart = useRef({ x: 0, y: 0 });
  // Only flipped true once travel distance exceeds DRAG_THRESHOLD px.
  const hasDragged = useRef(false);
  const DRAG_THRESHOLD = 3; // px

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Only primary button.
      if (e.button !== 0) return;
      isDragging.current = true;
      hasDragged.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current) return;
        const dx = ev.clientX - dragStart.current.x;
        const dy = ev.clientY - dragStart.current.y;
        // Upgrade to "drag" only once we've moved beyond the dead-zone.
        if (!hasDragged.current && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }
        if (hasDragged.current) {
          setPos({
            x: ev.clientX - dragOffset.current.x,
            y: ev.clientY - dragOffset.current.y,
          });
        }
      };

      const onMouseUp = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);

      // Prevent text selection while dragging.
      e.preventDefault();
    },
    [pos.x, pos.y],
  );

  const handleBadgeClick = useCallback(() => {
    // If the pointer travelled beyond the threshold this was a drag — ignore.
    if (hasDragged.current) return;
    setIsOpen((v) => !v);
  }, []);

  // ── SSR polling effect ──────────────────────────────────────────────────────
  useEffect(() => {
    async function pollSsrRequests() {
      // Skip polling when the tab is backgrounded. Complex host pages (e.g.
      // Checkout) can spend long stretches hidden while the user is on other
      // tabs; polling in that state just spams the network panel and burns
      // CPU on the eventual re-render. Fetching resumes on the next interval
      // tick once the tab is visible again.
      if (typeof document !== 'undefined' && document.hidden) return;

      try {
        const res = await fetch('/api/dev/api-debugger', { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          setSsrRequests(data);
        }
      } catch {
        // Swallow errors silently — the overlay should never break the app.
      }
    }

    // Run once immediately, then on each interval tick.
    pollSsrRequests();
    const intervalId = setInterval(pollSsrRequests, SSR_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [setSsrRequests]);

  // ── Merged request list (Client + SSR), sorted newest first ─────────────────
  const mergedRequests = useMemo<ApiRequest[]>(() => {
    return [...requests, ...ssrRequests].sort(
      (a, b) => b.startedAt - a.startedAt,
    );
  }, [requests, ssrRequests]);

  // ── Derived counts ──────────────────────────────────────────────────────────
  const activeCount = useMemo(
    () => mergedRequests.filter((r) => r.status === 'pending').length,
    [mergedRequests],
  );
  const totalCount = mergedRequests.length;
  const pendingCount = activeCount;

  const successCount = useMemo(
    () => mergedRequests.filter((r) => r.status === 'success').length,
    [mergedRequests],
  );

  const errorCount = useMemo(
    () => mergedRequests.filter((r) => r.status === 'error').length,
    [mergedRequests],
  );

  const ssrCount = useMemo(
    () => mergedRequests.filter((r) => r.environment === 'SSR').length,
    [mergedRequests],
  );

  const clientCount = useMemo(
    () => mergedRequests.filter((r) => r.environment === 'Client').length,
    [mergedRequests],
  );

  const filtered = useMemo(
    () =>
      filter === 'all'
        ? mergedRequests
        : mergedRequests.filter((r) => r.status === filter),
    [mergedRequests, filter],
  );

  // ── Clear handler — wipes both client Zustand state and server cache ────────
  const handleClear = useCallback(async () => {
    // Optimistically clear locally first for instant feedback.
    clearRequests();

    try {
      await fetch('/api/dev/api-debugger', { method: 'DELETE' });
    } catch {
      // Swallow — the server cache will be cleared on the next interaction.
    }
  }, [clearRequests]);

  const TABS: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: totalCount },
    { key: 'pending', label: 'Pending', count: pendingCount },
    { key: 'success', label: 'Success', count: successCount },
    { key: 'error', label: 'Error', count: errorCount },
  ];

  if (!isMounted || !enabled) return null;

  return (
    /*
     * ── Shared draggable container ──────────────────────────────────────────
     * Both the badge AND the expanded panel live inside this single fixed div.
     * The panel renders below the badge naturally via flex-col — no manual
     * top/left pixel arithmetic is needed.
     *
     * pointer-events: none on the container prevents it from inadvertently
     * eating clicks on the page backdrop; pointer-events: auto is restored on
     * the interactive children.
     */
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        transform: 'translateY(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {/* ── Floating badge (drag handle) ── */}
      <button
        id="api-debugger-badge"
        title="Toggle API Debugger"
        type="button"
        style={{
          pointerEvents: 'auto',
          cursor: isDragging.current ? 'grabbing' : 'grab',
        }}
        className="
          group bg-slate-900/90 relative flex items-center gap-2 rounded-full px-3 py-2 shadow-2xl ring-1 ring-white/10 backdrop-blur-md transition-all
          duration-200 select-none
          hover:scale-105 hover:ring-white/25
          active:scale-95
        "
        onMouseDown={handleMouseDown}
        onClick={handleBadgeClick}
      >
        {/* Activity indicator dot */}
        {activeCount > 0 ? (
          <PulsingDot />
        ) : (
          <span className="bg-emerald-500 size-2 rounded-full" />
        )}

        {/* Request counter label */}
        <span className="text-slate-200 font-mono text-[11px] leading-none font-semibold">
          {activeCount > 0 ? (
            <>
              <span className="text-amber-400">{activeCount}</span> /{' '}
              {totalCount}
            </>
          ) : (
            <>
              {totalCount} req{totalCount !== 1 ? 's' : ''}
            </>
          )}
        </span>

        {/* Module badge */}
        <span className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 rounded-sm border px-1 py-0.5 text-[9px] font-bold tracking-widest uppercase">
          API
        </span>
      </button>

      {/* ── Expanded panel — sits naturally below the badge via flex-col ── */}
      {isOpen && (
        <div
          id="api-debugger-panel"
          style={{
            pointerEvents: 'auto',
            width: 420,
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'min(600px, calc(100vh - 5rem))',
          }}
          className="
            bg-slate-900/95 flex flex-col overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10 backdrop-blur-xl
          "
        >
          {/* Panel header */}
          <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-white">
                API Debugger
              </span>
              {activeCount > 0 && <PulsingDot />}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="
                  text-slate-400 rounded-sm px-2 py-1 text-[10px] font-semibold tracking-wider uppercase transition
                  hover:bg-white/10 hover:text-white
                "
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                aria-label="Close API Debugger"
                type="button"
                className="
                  text-slate-400 flex size-6 items-center justify-center rounded-sm transition
                  hover:bg-white/10 hover:text-white
                "
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 border-b border-white/8 px-3 py-2">
            {TABS.map(({ key, label, count }) => (
              <button
                key={key}
                type="button"
                className={`
                  rounded-sm px-2 py-1 text-[10px] font-semibold transition-colors
                  ${
                    filter === key
                      ? 'bg-indigo-500/25 text-indigo-300 ring-indigo-500/40 ring-1'
                      : `
                        text-slate-400
                        hover:text-slate-200 hover:bg-white/8
                      `
                  }
                `}
                onClick={() => setFilter(key)}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {/* Request list */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <span className="text-2xl opacity-30">📡</span>
                <p className="text-slate-500 text-[11px]">
                  {totalCount === 0
                    ? 'No requests captured yet'
                    : 'No requests match this filter'}
                </p>
              </div>
            ) : (
              filtered.map((req) => <RequestRow key={req.id} req={req} />)
            )}
          </div>

          {/* Panel footer */}
          <div className="flex items-center justify-between border-t border-white/8 px-4 py-2">
            <span className="text-slate-600 font-mono text-[9px] tracking-widest uppercase">
              {process.env.NODE_ENV === 'development' ? 'dev mode' : 'forced debug mode'}
            </span>
            <span className="text-slate-600 font-mono text-[9px]">
              {ssrCount > 0 && (
                <span className="text-purple-500/70">{ssrCount} SSR</span>
              )}
              {ssrCount > 0 && clientCount > 0 && (
                <span className="text-slate-700"> · </span>
              )}
              {clientCount > 0 && (
                <span className="text-cyan-500/70">{clientCount} Client</span>
              )}
              {ssrCount === 0 && clientCount === 0 && (
                <span>{totalCount} total · {activeCount} in-flight</span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
